import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Checkbox, color as colors, Panel } from "@workboard/wobo-ui";
import Term from "./Term";
import SelectFilter from "../../SelectFilter";

const Filters = styled("section")``;
const Group = styled("section")``;
const Section = styled("section")``;
const Content = styled("section")``;

const List = styled("ul")``;

const Total = styled("li")``;
const Green = styled("li")``;
const LGreen = styled("li")``;
const Amber = styled("li")``;
const Red = styled("li")``;

const OKRs = styled(Term)``;
const KRs = styled(Term)``;
const FuncTeamsOKRs = styled(Term)``;
const WorkingGroupsOKRs = styled(Term)``;
const UserWithOKRs = styled(Term)``;

const Stats = ({
  className,
  options,
  onChange,
  okrCount = 0,
  krCount = 0,
  okrGreenCount = 0,
  krGreenCount = 0,
  okrLightGreenCount = 0,
  krLightGreenCount = 0,
  okrAmberCount = 0,
  krAmberCount = 0,
  okrRedCount = 0,
  krRedCount = 0,
  funTeamOkr = 0,
  wgTeamOkr = 0,
  userCount = 0
}) => {
  const { t } = useTranslation();

  const handleChange = useCallback(
    key => value => {
      onChange({ [key]: value });
    },
    [onChange]
  );

  const handleCheckbox = useCallback(
    key => (ev, value) => {
      onChange({ [key]: value });
    },
    [onChange]
  );

  return (
    <Panel as={Section} className={className}>
      <Filters>
        <SelectFilter
          key={options}
          label="Show"
          options={options}
          onChange={handleChange(`filterOKRs`)}
        />
        <Group>
          <Checkbox
            data-testid='checkbox-open-okrs'
            label={`Open ${t("OKRs")}`}
            isDefaultChecked={true}
            onChange={handleCheckbox(`openOKRs`)}
            />
          <Checkbox
            data-testid='checkbox-closed-okrs'
            label={`Closed ${t("OKRs")}`}
            onChange={handleCheckbox(`closedOKRs`)}
          />
        </Group>
      </Filters>
      <Content>
        <OKRs
          title={`Team ${t("OKRs")}`}
          helperText={`This is the number of ${t(
            "OKRs"
          )} belonging to functional teams and working group ${t(
            "OKRs"
          )} for the selected time period and filters. The Total number will include green, amber, red ${t(
            "OKRs"
          )}, as well as any grey ${t("OKRs")} (where the ${t(
            "KR"
          )} sources are still pending).`}
        >
          <List>
            <Total>{okrCount} <span>{t("Total")}</span></Total>
            <Green>{okrGreenCount} <span>{t("Green")}</span></Green>
            <LGreen>{okrLightGreenCount} <span>{t("Light Green")}</span></LGreen>
            <Amber>{okrAmberCount} <span>{t("Amber")}</span></Amber>
            <Red>{okrRedCount} <span>{t("Red")}</span></Red>
          </List>
        </OKRs>
        <KRs
          title={`Team ${t("Key Results")}`}
          helperText={`This is the number of ${t(
            "key results"
          )} belonging to functional teams and working group ${t(
            "key results"
          )} for the selected time period and filters. The Total number will include green, amber, red ${t(
            "key results"
          )}, as well as any grey ${t("key results")} (where the ${t(
            "KR"
          )} sources are still pending).`}
        >
          <List>
            <Total>{krCount} <span>{t("Total")}</span></Total>
            <Green>{krGreenCount} <span>{t("Green")}</span></Green>
            <LGreen>{krLightGreenCount} <span>{t("Light Green")}</span></LGreen>
            <Amber>{krAmberCount} <span>{t("Amber")}</span></Amber>
            <Red>{krRedCount} <span>{t("Red")}</span></Red>
          </List>
        </KRs>
        <FuncTeamsOKRs
          title={`Functional Teams with ${t("OKRs")}`}
          helperText={`This is the number of functional teams (teams part of the org chart) with ${t(
            "OKRs"
          )} for the selected time period and filters.`}
        >
          {funTeamOkr}
        </FuncTeamsOKRs>
        <WorkingGroupsOKRs
          title={`Working Groups with ${t("OKRs")}`}
          helperText={`This is the number of custom teams (teams not part of the org chart) with ${t(
            "OKRs"
          )} for the selected time period and filters.`}
        >
          {wgTeamOkr}
        </WorkingGroupsOKRs>
        <UserWithOKRs
          title={`Users with Active ${t("OKRs")}`}
          helperText={`Count of users who are part of functional teams and working groups with open ${t(
            "objectives"
          )} for the selected time period, or own personal ${t("objectives")}.`}
        >
          {userCount}
        </UserWithOKRs>
      </Content>
    </Panel>
  );
};

export default styled(Stats)`
  padding: 0;
  grid-gap: 0;

  ${SelectFilter} {
    > span {
      margin-bottom: 6px;
    }
  }

  ${Checkbox} {
    margin-left: 10px;
  }

  ${Filters} {
    align-items: center;
    border-bottom: 1px solid ${colors("--black-opacity-14")};
    display: flex;
    justify-content: space-between;
    padding: 8px 16px;
  }

  ${Content} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 16px 8px;
    margin: 16px;

    ${OKRs} {
      grid-column: 1;
      grid-row: 1;
    }

    ${KRs} {
      grid-column: 2;
      grid-row: 1;
    }

    ${FuncTeamsOKRs} {
      grid-column: 1;
      grid-row: 2;
    }
    ${WorkingGroupsOKRs} {
      grid-column: 2;
      grid-row: 2;
    }
  }

  ${List} {
    > li {
      font-weight: bold;
      margin-bottom: 4px;
    }

    ${Total} {
      font-size: 24px;

      > span {
        font-size: 16px;
        font-weight: bold;
        margin-left: .3em;
      }
    }

    ${Green}, ${LGreen}, ${Amber}, ${Red} {
      font-size: 20px;

      > span {
        font-size: 14px;
        font-weight: bold;
        margin-left: .3em;
      }
    }

    ${Green} {
      color: #369933;
    }

    ${LGreen} {
      color: #9c6;
    }

    ${Amber} {
      color: #f93;
    }

    ${Red} {
      color: #d33933;
    }
`;
